import React from "react";

import { HighlightOffOutlined } from "@mui/icons-material";
import {
  Box,
  ButtonBase,
  Card,
  IconButton,
  Popover,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from "@mui/material";
import PopupState, { bindMenu, bindTrigger } from "material-ui-popup-state";

import { CustomSlider } from "../../common";
import Tooltip from "../../common/Tooltip";
import { getCamelCaseWord } from "../../utils/functions";

/* =============================================================================
<JobCriteriaPopup />
============================================================================= */
const JobCriteriaPopup = ({ post, children }) => {
  // console.log(post)
  return (
    <PopupState variant="popper">
      {(popupState) => (
        <>
          <ButtonBase {...bindTrigger(popupState)}>{children}</ButtonBase>

          <Popover {...bindMenu(popupState)}>
            <Card sx={{ width: "650px", p: 2 }}>
              <Box
                display="flex"
                justifyContent="space-between"
                alignItems="center"
              >
                <Typography fontSize="14px" fontWeight={600}>
                  Passing Criteria
                </Typography>
                <IconButton onClick={popupState.close}>
                  <HighlightOffOutlined sx={{ color: "primary.main" }} />
                </IconButton>
              </Box>
              <Box border={1} p={2} borderColor="#F2F2F2" mb={3}>
                {post?.passing_criteria?.resume_passing_criteria && (
                  <Box display="flex" alignItems="center" gap={4}>
                    <Typography fontSize="12px" fontFamily="Outfit" flex={0.4}>
                      Resume passing criteria
                    </Typography>
                    <CustomSlider
                      value={post?.passing_criteria?.resume_passing_criteria}
                      disabled
                      min={20}
                      max={99}
                      sx={{ flex: 1 }}
                      valueLabelDisplay="on"
                    />
                    <Typography fontSize="12px" fontFamily="Outfit">
                      {post?.passing_criteria?.resume_passing_criteria}%
                    </Typography>
                  </Box>
                )}
                {post?.passing_criteria?.interview_passing_criteria && (
                  <Box display="flex" alignItems="center" gap={4}>
                    <Typography fontSize="12px" fontFamily="Outfit" flex={0.4}>
                      Assessment passing criteria
                    </Typography>
                    <CustomSlider
                      value={post?.passing_criteria?.interview_passing_criteria}
                      disabled
                      min={20}
                      max={99}
                      sx={{ flex: 1 }}
                      valueLabelDisplay="auto"
                    />
                    <Typography fontSize="12px" fontFamily="Outfit">
                      {post?.passing_criteria?.resume_passing_criteria}%
                    </Typography>
                  </Box>
                )}
              </Box>
              <Typography color="#595959" fontSize="14px" fontWeight={600}>
                Ideal Candidate Persona
              </Typography>
              <TableContainer
                sx={{
                  maxHeight: "100%",
                  maxWidth: "100%",
                  fontFamily: "Outfit",
                  overflow: "hidden",
                  bgcolor: "white",
                }}
              >
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell align="left" sx={{ bgcolor: "white" }}>
                        <Typography
                          fontSize="14px"
                          fontWeight={600}
                          color="#21282F"
                        >
                          Attributes
                        </Typography>
                      </TableCell>
                      <TableCell align="left" sx={{ bgcolor: "white" }}>
                        <Typography
                          fontSize="14px"
                          fontWeight={600}
                          color="#21282F"
                        >
                          Requirements
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "160px", bgcolor: "white" }}
                      >
                        <Typography
                          fontSize="14px"
                          fontWeight={600}
                          color="#21282F"
                        >
                          Value
                        </Typography>
                      </TableCell>
                      <TableCell
                        align="left"
                        sx={{ width: "200px", bgcolor: "white" }}
                      >
                        <Typography
                          fontSize="14px"
                          fontWeight={600}
                          color="#21282F"
                        >
                          Weightage
                        </Typography>
                      </TableCell>
                      <TableCell align="left" sx={{ bgcolor: "white" }}>
                        <Typography
                          fontSize="14px"
                          fontWeight={600}
                          color="#21282F"
                        >
                          Distribution
                        </Typography>
                      </TableCell>
                    </TableRow>
                  </TableHead>

                  <TableBody>
                    {post?.job_criteria?.map((row, rowIndex) => (
                      <TableRow key={rowIndex}>
                        <TableCell sx={{ fontSize: "11px", fontWeight: 500 }}>
                          {getCamelCaseWord(row.name)}
                        </TableCell>
                        <TableCell sx={{ fontSize: "11px" }}>
                          {Array.isArray(row.value) ? (
                            <Tooltip
                              title={
                                <Box display="flex" gap={1}>
                                  {row.value.map((r) => (
                                    <Typography
                                      key={r}
                                      color="#37474F"
                                      bgcolor="#F4F4F4"
                                      fontSize="13px"
                                      px={1}
                                      py={0.5}
                                    >
                                      {r}
                                    </Typography>
                                  ))}
                                </Box>
                              }
                            >
                              <Typography
                                fontSize="11px"
                                fontWeight={500}
                                display="inline"
                                mr={0.5}
                                bgcolor="#F4F4F4"
                                p={0.5}
                              >
                                {row.value[0]}
                              </Typography>
                              <span style={{ fontSize: "10px" }}>
                                {row.value.length - 1}+
                              </span>
                            </Tooltip>
                          ) : (
                            row.value
                          )}
                        </TableCell>
                        <TableCell>
                          <Box>
                            <Box display="flex">
                              <Box
                                sx={{
                                  border: 1,
                                  borderRadius: "30px",
                                  p: 1,
                                  borderColor: row?.compulsory
                                    ? "#762FDE"
                                    : "#3EBDD8",
                                }}
                              >
                                <Typography
                                  color={
                                    row?.compulsory ? "#762FDE" : "#3EBDD8"
                                  }
                                  fontSize="9px"
                                  fontWeight={600}
                                >
                                  {row.compulsory
                                    ? "Compulsory"
                                    : "Good to have"}
                                </Typography>
                              </Box>
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell>
                          <div
                            style={{
                              display: "flex",
                              height: "20px",
                              width: "100%",
                              borderRadius: "50px",
                              overflow: "hidden",
                            }}
                          >
                            {[1, 2, 3, 4, 5].map((num) => (
                              <div
                                key={num}
                                className="progress-box"
                                style={{
                                  flex: "1",
                                  backgroundColor:
                                    num <=
                                    Math.ceil(
                                      (parseInt(row.icp_weightage) / 100) * 5
                                    )
                                      ? "#85E4A5"
                                      : "#F1F1F1",
                                  color:
                                    num <=
                                    Math.ceil(
                                      (parseInt(row.icp_weightage) / 100) * 5
                                    )
                                      ? "white"
                                      : "#858F94",
                                  display: "flex",
                                  justifyContent: "center",
                                  alignItems: "center",
                                  fontWeight: "bold",
                                  cursor: "pointer",
                                  transition:
                                    "background-color 0.3s ease, color 0.3s ease",
                                  marginRight: num < 5 ? "2px" : "0",
                                  width: "20%",
                                }}
                              />
                            ))}
                          </div>
                        </TableCell>
                        <TableCell sx={{ fontSize: "11px" }}>
                          {row.icp_weightage}%
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Card>
          </Popover>
        </>
      )}
    </PopupState>
  );
};
/* Export
============================================================================= */
export default JobCriteriaPopup;
